import { inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { defer, Observable } from 'rxjs';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';

export interface LocalizationData {
  /**
   * Observable of the current language.
   */
  currentLanguage$: Observable<string>;

  /**
   * The current language. Use this if you need immediate access to the current language.
   */
  currentLanguage: string;

  /**
   * The translate service.
   */
  translateService: TranslateService;
}

export function useLocalization(): LocalizationData {
  const translateService = inject(TranslateService);
  // `defer` evaluates the seed at SUBSCRIBE time, so late subscribers (and
  // take(1) snapshot reads) see the current language, not the language at the
  // moment `useLocalization()` ran. TranslateService itself holds the live
  // state; this stream is a stateless derivation of it.
  const currentLanguage$: Observable<string> = defer(() =>
    translateService.onLangChange.pipe(
      map(() => translateService.currentLang),
      startWith(translateService.currentLang || localStorage.getItem('user_language') || 'en'),
      distinctUntilChanged()
    )
  );

  return {
    currentLanguage$,
    get currentLanguage(): string {
      return translateService.currentLang || 'en';
    },
    translateService,
  };
}
